// src/components/businesses/MarketingSafetyPanel.tsx
// Panel de dos toggles críticos para el pipeline de marketing
// automático:
//
//   · marketing_live_enabled — si OFF, las tasks de Meta Ads no llaman
//     APIs externas (dry-run). Afecta: publicación de posts, push de
//     campañas, webhooks salientes.
//   · ai_optimizer_enabled — si OFF, las 5 loops del optimizer
//     (anomaly / rotate_creatives / budgets / diagnose / cycle)
//     filtran esta org. cycle es la única que muta Meta; apagar
//     este flag aísla la org del auto-pilot.
//
// Ambos default a false — tier Growth+ los prende.

import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Save, Shield } from 'lucide-react';

import {
  ApiError, getMarketingSafety, updateMarketingSafety,
  type MarketingSafety,
} from '@/lib/api_businesses';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function MarketingSafetyPanel({ addNotification }: { addNotification?: Notifier }) {
  const [cfg, setCfg] = useState<MarketingSafety | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMarketingSafety().then(setCfg).catch((e) => {
      addNotification?.({
        type: 'error', title: 'No se pudo cargar',
        message: e instanceof ApiError ? e.message : e?.message,
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg) return;
    setBusy(true);
    try {
      const out = await updateMarketingSafety(cfg);
      setCfg(out);
      addNotification?.({ type: 'success', title: 'Configuración guardada' });
    } catch (e: any) {
      addNotification?.({
        type: 'error', title: 'No se pudo guardar',
        message: e instanceof ApiError ? e.message : e?.message,
      });
    } finally { setBusy(false); }
  };

  if (loading || !cfg) {
    return (
      <div className="py-8 flex justify-center text-white/40 text-sm">
        <Loader2 size={14} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <div className="o-card space-y-3">
        <div className="flex items-center gap-2">
          <Shield size={18} style={{ color: 'var(--acc1)' }} />
          <h2 className="text-lg font-bold text-white">Seguridad de Marketing</h2>
        </div>
        <p className="text-white/50 text-sm">
          Dos flags que controlan si las automatizaciones tocan Meta real.
          Útil para apagar el piloto sin deshacer la configuración.
        </p>

        {/* marketing_live_enabled */}
        <div className="border border-white/10 rounded-lg p-3 space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={cfg.marketing_live_enabled}
              onChange={(e) =>
                setCfg({ ...cfg, marketing_live_enabled: e.target.checked })
              }
            />
            <div className="flex-1">
              <div className="text-white font-medium text-sm">Modo live</div>
              <p className="text-xs text-white/50">
                Cuando está <strong>OFF</strong>, las tasks de marketing no llaman
                APIs externas (solo loguean el payload). Activa esto para publicar
                posts reales a Meta + lanzar campañas.
              </p>
            </div>
          </label>
        </div>

        {/* ai_optimizer_enabled */}
        <div className="border border-white/10 rounded-lg p-3 space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={cfg.ai_optimizer_enabled}
              onChange={(e) =>
                setCfg({ ...cfg, ai_optimizer_enabled: e.target.checked })
              }
            />
            <div className="flex-1">
              <div className="text-white font-medium text-sm">Optimizer IA activo</div>
              <p className="text-xs text-white/50">
                Habilita los 5 loops del optimizer (detección de anomalías,
                rotación de creativos con Thompson Sampling, reallocation de
                budget por ROAS, diagnóstico LLM, ciclo maestro que aplica
                cambios a Meta). <strong>cycle</strong> es el único que
                muta Meta — tiene guardrails: max 5 acciones por corrida y
                no auto-pausa campañas con spend 14d ≥ \$1000.
              </p>
              {cfg.ai_optimizer_enabled && !cfg.marketing_live_enabled && (
                <div className="mt-2 flex items-center gap-1 text-amber-300/90 text-xs">
                  <AlertTriangle size={11} />
                  Requiere además "Modo live" para que cycle pueda ejecutar
                  acciones reales.
                </div>
              )}
            </div>
          </label>
        </div>

        <button
          onClick={save}
          disabled={busy}
          className="px-4 py-2 rounded-lg bg-[var(--acc1)]/20 hover:bg-[var(--acc1)]/30 border border-[var(--acc1)]/30 text-[var(--acc1)] flex items-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
        </button>
      </div>
    </div>
  );
}

export default MarketingSafetyPanel;
